import * as _ from "lodash";
import { HashMap } from "../../shared/model/HashMap";

import { ThunkResult } from "../../shared/utilities";
import { BpmnJsCliWrapper } from "../model/BpmnJsCliWrapper";
import { FrendsRuleProvider } from "../model/FrendsRules/FrendsRuleProvider";
import { IAction } from "../model/IAction";
import { ConnectionTypes, ElementType, IElement, IElementDictionary, TypesWithRequiredNames, TypesWithSelectedType, TypesWithUniqueNames } from "../model/IProcessData";
import { Process } from "../model/Process";
import { getProcessReferences, getTriggerReferences } from "../model/References";
import { State } from "../model/State";
import { ValidationError, ValidationErrorDictionary, ValidationErrorType } from "../model/ValidationData";
import { validateProcessVariables } from "./ValidateProcessVariables";
import { IElementVariable, IVariableScopeDictionary, populateAccessibleReturnsAndVariables, VariableScope } from "./VariableScopeAnalyzer";
import { updateVariableScopes } from "./VariableScopeAnalyzer";

import validateNativeAi from "./validateNativeAi";
import { Triggers } from "processEditor/model/ITriggerData";
import { getIntermediateCatchEventEventDefinitionType } from "processEditor/ElementTypeHelpers";
import { getElementName } from "processEditor/components/Helpers";
import { isManualTriggerDefaultValueKey } from "processEditor/model/ManualTrigger";
import { runTriggerValidators } from "./triggerValidationActions";

type ConnectionDirection = "incoming" | "outgoing";

export const VALIDATE_PROCESS = "VALIDATE_PROCESS";
export const VALIDATE_PROCESS_DEFERRED = "VALIDATE_PROCESS_DEFERRED";

export function rebuildVariableScopesAndTriggerValidationAction(): ThunkResult<void, State> {

    return (dispatch, getState) => {
        dispatch(updateVariableScopes()); // Rebuils the variable scopes before the validation happens
        dispatch(validationAction(getState()));
    };
}

export function validationAction(oldState: State): ValidateProcessAction {
    const clonedState = _.cloneDeep(oldState);
    let results: ValidationError[] = [];
    if (clonedState.validation.elementIdsToValidate.indexOf("process") >= 0) {
        results = results.concat(validateProcess(clonedState.process));
    }
    const elementsToValidate = getElementsToValidate(clonedState.elements, clonedState.validation.elementIdsToValidate);
    results = results.concat(validateTriggers(elementsToValidate, clonedState));
    results = results.concat(validateConnections(elementsToValidate));
    results = results.concat(validateElementNamesAndState(clonedState, elementsToValidate));
    results = results.concat(validateLoops(elementsToValidate));
    results = results.concat(validateHydrationPointAndSignalRehydrationCorrelationIds(elementsToValidate));

    const nativeAiElements = elementsToValidate.filter(element =>
        element && element.type === ElementType.NativeAi
    );
    nativeAiElements.forEach(element => {
        if (element) {
            const nativeAiValidationErrors: ValidationError[] = [];
            validateNativeAi({ element, aiModels: clonedState.nativeAiData.models, validationErrors: nativeAiValidationErrors });
            results = results.concat(nativeAiValidationErrors);
        }
    });

    const elementsAndParametersToValidate = elementsToValidate
        .concat(getElementParametersToValidate(clonedState.elements, clonedState.validation.elementIdsToValidate));
    results = results.concat(validateParameterReferences(clonedState, elementsAndParametersToValidate));
    const result = {} as ValidationErrorDictionary;
    for (const r of results) {
        if (!result[r.elementId as string]) {
            result[r.elementId as string] = [] as ValidationError[];
        }
        result[r.elementId as string].push(r);
    }

    const processVariableResults = validateProcessVariables(clonedState.processVariables ?? [], clonedState.environmentVariables);
    if (processVariableResults.length > 0) {
        result["processVariables"] = processVariableResults
    }

    return {
        type: VALIDATE_PROCESS,
        validationDictionary: result
    } as ValidateProcessAction;
}

// Contains heavier validation actions that should not be run on every change
export function deferredValidationAction(oldState: State): ValidateProcessAction {
    let results: ValidationError[] = [];
    const clonedState = _.cloneDeep(oldState);
    const elementsToValidate = getElementsToValidate(clonedState.elements, clonedState.validation.elementIdsToValidate);

    results = results.concat(validateIntermediateReturnWithHydrationPoint(elementsToValidate));
    results = results.concat(validateParallelBranches(elementsToValidate));    

    const validationErrorDictionary = {} as ValidationErrorDictionary;
    for (const r of results) {
        if (!validationErrorDictionary[r.elementId as string]) {
            validationErrorDictionary[r.elementId as string] = [] as ValidationError[];
        }
        r.messageString = r.getMessageString() + " Once you've corrected the problem, click 'Validate' again to clear the validation error.";
        validationErrorDictionary[r.elementId as string].push(r);
    }

    return {
        type: VALIDATE_PROCESS_DEFERRED,
        validationDictionary: validationErrorDictionary
    } as ValidateProcessAction;
}

export interface ValidateProcessAction extends IAction {
    validationDictionary: ValidationErrorDictionary;
}

export const ADD_ELEMENTS_TO_VALIDATION = "ADD_ELEMENTS_TO_VALIDATION";

export function addElementIdsToValidation(elementIds: string[]): AddElementsToValidationAction {
    return {
        type: ADD_ELEMENTS_TO_VALIDATION,
        elementIds: elementIds
    } as AddElementsToValidationAction;
}

export interface AddElementsToValidationAction extends IAction {
    elementIds: string[];
}

/// Validation:
export function validateConnections(elements: Array<IElement>): ValidationError[] {
    const errorElements: ValidationError[] = [];

    let firstStartShapeId: string = "";
    for (const element of elements) {

        let bpmnElement: BpmnJsElement | undefined;
        try {
            bpmnElement = BpmnJsCliWrapper.element(element?.id);
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.log("Could not find element " + element?.id + ", skipping validation for it.");
            continue;
        }
        if (!bpmnElement) {
            continue;
        }

        if (!ConnectionTypes.find((x) => x === element?.type)) { // The element is not of connection type:

            if (element?.type === ElementType.Start && bpmnElement.outgoing && bpmnElement.outgoing.length > 0) {
                const nextShapeId = bpmnElement.outgoing[0].target!.id;
                if (!firstStartShapeId) {
                    firstStartShapeId = nextShapeId;
                } else {
                    if (firstStartShapeId !== nextShapeId) {
                        // different target to the start shape than the first start shape!
                        errorElements.push(new ValidationError(ValidationErrorType.TriggersMustPointToSameTarget, bpmnElement.id));
                    }
                }
            }

            // Startnodes should not have incoming connections, other tracked element types should (excluding datastore and dataobject references):
            if (element?.type !== ElementType.Start &&
                element?.type !== ElementType.SubProcessStartNode) {
                if (bpmnElement.incoming?.length === 0) {
                    errorElements.push(new ValidationError(ValidationErrorType.NoIncomingSequences, element?.id));
                }
            }
            // All nodes except return and throw should have outgoing sequences (excluding datastore and dataobject references)
            if (element?.type !== ElementType.Return &&
                element?.type !== ElementType.Throw &&
                element?.type !== ElementType.IntermediateReturn) {
                if (bpmnElement.outgoing?.length === 0) {
                    errorElements.push(new ValidationError(ValidationErrorType.NoOutgoingSequences, element?.id));
                }
            }

            if (element?.type === ElementType.IntermediateReturn) {
                // check the element really really is an intermediate return shape, and not just some wrongly copied one (ACC-7279)
                if (bpmnElement.businessObject.eventDefinitions &&
                    bpmnElement.businessObject.eventDefinitions.find((e) => e.$type === "bpmn:SignalEventDefinition")) {

                    if (bpmnElement.incoming?.length !== 1) {
                        errorElements.push(new ValidationError(ValidationErrorType.OnlyOneIncomingConnection, element.id));
                    } else {
                        const source = bpmnElement.incoming[0].source;
                        if (source && source.outgoing && source.outgoing.length < 2) {
                            errorElements.push(new ValidationError(ValidationErrorType.IntermediateReturnCannotBeOnlyConnection, source.id));
                        }
                    }
                }
            }

            if (element?.type === ElementType.ScheduledRehydration) {
                const outgoing = bpmnElement.outgoing;
                if (outgoing && outgoing.length > 0) {
                    // Scheduled rehydration can only connect to a hydration point and nothing else
                    if (outgoing.length > 1 || (outgoing.length === 1 && outgoing[0].target && getIntermediateCatchEventEventDefinitionType(outgoing[0].target) !== ElementType.HydrationPoint)) {
                        errorElements.push(new ValidationError(ValidationErrorType.InvalidOutgoingConnectionForScheduledRehydration, element?.id));
                    }
                    else {
                        // Scheduled rehydration can only connect to the first preceding hydration point. 
                        // For example in the following case SR can only connect to HP2:
                        // Start -> Task -> HP1 -> Task -> HP2 -> Task -> SR -> ...
                        const previousHydrationPoint = findUpstreamMatch(bpmnElement, (elem) => getIntermediateCatchEventEventDefinitionType(elem) == ElementType.HydrationPoint);
                        if (previousHydrationPoint?.id !== outgoing[0].target?.id) {
                            errorElements.push(new ValidationError(ValidationErrorType.InvalidOutgoingConnectionForScheduledRehydration, element?.id));
                        }

                        // At least one Exclusive Decision is needed between preceding Hydration Point and Scheduled rehydration
                        const hydrationPointOrExclusiveGateway = findUpstreamMatch(bpmnElement, elem => elem.id === previousHydrationPoint?.id || elem.type === "bpmn:ExclusiveGateway");
                        if (hydrationPointOrExclusiveGateway === previousHydrationPoint) {
                            errorElements.push(new ValidationError(ValidationErrorType.MissingExclusiveDecisionBeforeScheduledRehydration, element?.id))
                        }
                    }
                } else {
                    errorElements.push(new ValidationError(ValidationErrorType.NoOutgoingConnectionsFromScheduledRehydration, element?.id));
                }
            }
        }
    }

    return errorElements;
}

export function validateLoops(elementsToValidate: Array<IElement>): ValidationError[] {
    if (!elementsToValidate || elementsToValidate.length < 1) {
        return [];
    }
    let startLoopWalkShape = elementsToValidate.find((e) => e?.type === ElementType.Start); // start check from first start, if set
    if (!startLoopWalkShape) {
        // just get the first
        startLoopWalkShape = elementsToValidate[0];
    }

    let bpmnElement: BpmnJsElement | undefined;
    try {
        bpmnElement = BpmnJsCliWrapper.element(startLoopWalkShape?.id);
    } catch (error) {
        // tslint:disable-next-line:no-console
        console.log("Could not find element " + startLoopWalkShape?.id + ", skipping validation for it.");
        return [];
    }
    if (!bpmnElement) {
        return [];
    }

    const firstConnection = bpmnElement.outgoing && bpmnElement.outgoing.length > 0 && bpmnElement.outgoing[0];
    if (!firstConnection || !firstConnection.target) {
        // no connections
        return [];
    }

    const visitedShapes = [startLoopWalkShape?.id];
    const error = FrendsRuleProvider.walkConnectionLoop(firstConnection.target, visitedShapes, startLoopWalkShape?.id);

    if (!error) {
        return [];
    }

    return [error];
}

export function validateIntermediateReturnWithHydrationPoint(elementsToValidate: Array<IElement>): ValidationError[] {
    const errors: ValidationError[] = [];

    if (elementsToValidate.some(element => element?.type === ElementType.HydrationPoint)) {

        // A Process must produce an intermediate result if 
        //  - The trigger expects a result (HTTP/API)
        //  - Consume message immediately is enabled (ServiceBus)
        // The intermediate result must be returned before process dehydrates
        const intermediateReturnRequired = elementsToValidate.find(element =>
            element &&
            element.type == ElementType.Start &&
            element.selectedTypeId && (
                [Triggers.HttpTrigger.triggerType, Triggers.HttpApiTrigger.triggerType].includes(element.selectedTypeId) ||
                (element.selectedTypeId === Triggers.ServiceBusTrigger.triggerType && element.parameters["consumeMessageImmediately"] === true)
            ));

        if (intermediateReturnRequired) {
            const intermediateReturns = elementsToValidate.filter(element => element?.type === ElementType.IntermediateReturn);
            if (intermediateReturns.length === 0) {
                errors.push(
                    new ValidationError(ValidationErrorType.InvalidProcessWithHydrationPointAndTrigger, intermediateReturnRequired.id));
            } else {
                const startElement = elementsToValidate.find((e) => e?.type === ElementType.Start);
                const startBpmnElement: BpmnJsElement | undefined = BpmnJsCliWrapper.element(startElement?.id);

                const hydrationPoint = findFirstHydrationPoint(startBpmnElement);

                // Validate that intermediate return is properly placed before the first hydration point
                if (hydrationPoint) {
                    const errorMessage = validateIntermediateReturnBeforeHydrationPoint(startBpmnElement, hydrationPoint, elementsToValidate);
                    if (errorMessage) {
                        errors.push(new ValidationError(
                            ValidationErrorType.InvalidProcessWithHydrationPointAndTrigger,
                            hydrationPoint.id,
                            null,
                            undefined,
                            errorMessage
                        ));
                    }
                }

                // Check that no intermediate returns exist after hydration points
                intermediateReturns.forEach((returnElement: IElement) => {
                    const element: BpmnJsElement | undefined = BpmnJsCliWrapper.element(returnElement?.id);
                    if (element && hasUpstreamMatch(element, (el) => {
                        if (getIntermediateCatchEventEventDefinitionType(el) !== ElementType.HydrationPoint) {
                            return false;
                        }
                        // IR can be after if HP is direct parent of it (IR is attached to HP)
                        if (element.incoming) {
                            for (const incoming of element.incoming) {
                                if (incoming.source?.id === el.id) {
                                    return false;
                                }
                            }
                        }
                        return true;
                    })) {
                        errors.push(
                            new ValidationError(ValidationErrorType.InvalidProcessWithHydrationPointAndTrigger, returnElement.id,
                                null, undefined, "There can't be an Intermediate Return after a Checkpoint."));
                    }
                });
            }
        }

        // reply settings are not allowed for a process with hydration point 
        const queueTriggersWithReplyFound = elementsToValidate.find(element =>
            element &&
            element.type == ElementType.Start &&
            element.selectedTypeId &&
            [Triggers.RabbitMQTrigger.triggerType, Triggers.ServiceBusTrigger.triggerType, Triggers.QueueTrigger.triggerType].includes(element.selectedTypeId) &&
            (element.parameters["reply"] === true || element.parameters["replyErrors"] === true || element.parameters?.["replyTo"]?.trim())
        );

        if (queueTriggersWithReplyFound) {
            errors.push(
                new ValidationError(ValidationErrorType.InvalidProcessWithHydrationPointAndTrigger, queueTriggersWithReplyFound.id,
                    null, undefined, "Reply settings are not allowed for a Process with a Checkpoint."));
        }
    }

    return errors;
}

export function validateParallelBranches(elementsToValidate: Array<IElement>): ValidationError[] {
    const errors: ValidationError[] = [];

    // start traversal from first start, if set
    let startShape = elementsToValidate.find((e) => e?.type === ElementType.Start);
    if (!startShape) {
        // or just get the first
        startShape = elementsToValidate[0];
    }

    let start: BpmnJsElement | undefined;
    try {
        start = BpmnJsCliWrapper.element(startShape?.id);
    } catch (error) {
        // tslint:disable-next-line:no-console
        console.log("validateParallelBranches: Could not find element " + startShape?.id + ", skipping parallel branch validation.");
        return errors;
    }
    if (!start) {
        return errors;
    }
    
    const visited = new Set<string>();
    const stack = [start];

    // Loops elements one by one looking for a parallel split
    // Parallel split = element with two or more outgoing arrows
    while (stack.length > 0) {
        const current = stack.pop();

        if (!current || visited.has(current.id)) {
            continue;
        }

        visited.add(current.id);

        const outgoing = getValidParallelBranchTraversalConnections(current, "outgoing");

        // Look for a parallel split
        if (outgoing.length > 1) {

            const parallelPaths = outgoing
                .map(arrow => arrow.target ? followParallelBranch(arrow.target, new Set(), elementsToValidate) : [])
                .filter(branch => branch.length > 0); // Ignore empty branches

            if (parallelPaths.length > 1) {
                // Hydration point is not allowed in parallel branch
                const branchesWithHydrationPoint: Array<[BpmnJsElement[], BpmnJsElement]> = parallelPaths.flatMap(path => {
                    const hydrationPoint = path.find(el => getIntermediateCatchEventEventDefinitionType(el) === ElementType.HydrationPoint);
                    return hydrationPoint ? [[path, hydrationPoint]] : [];
                })

                for (const [path, hydrationPoint] of branchesWithHydrationPoint) {
                    errors.push(new ValidationError(null, hydrationPoint.id, null, undefined, `Checkpoint found in parallel branch: ${[current, ...path].map(el => getElementDisplayName(el, elementsToValidate.find(e => e.id === el.id))).join(" → ")
                        }.`));
                }
            }
        }

        // Continue linear traversal
        for (const arrow of outgoing) {
            if (arrow.target) {
                stack.push(arrow.target);
            }
        }
    }
  
    return errors;
}

function getElementDisplayName(bpmnJsElement: BpmnJsElement, element?: IElement): string {
    const displayName = element ? getElementName(element.type) : bpmnJsElement.id;
    const name = bpmnJsElement.businessObject.name ? ` (${bpmnJsElement.businessObject.name})` : "";

    // e.g. Checkpoint (<name of the checkpoint shape if set>)
    return `${displayName}${name}`;
}

/**
 * Finds the first hydration point in the process graph by traversing from the start element.
 * Uses breadth-first search to ensure we find the chronologically first hydration point.
 * This is required because elements in elementsToValidate can be in same order as they were added to the process, not in execution order.
*/
function findFirstHydrationPoint(startBpmnElement: BpmnJsElement | undefined): BpmnJsElement | undefined {
    if (startBpmnElement === undefined) {
        return undefined;
    }

    const visited = new Set<string>();
    const queue: BpmnJsElement[] = [startBpmnElement];

    while (queue.length > 0) {
        const current = queue.shift()!;

        if (!current || visited.has(current.id)) {
            continue;
        }

        visited.add(current.id);

        if (getIntermediateCatchEventEventDefinitionType(current) === ElementType.HydrationPoint) {
            return current;
        }

        const outgoing = current.outgoing || [];
        for (const connection of outgoing) {
            if (connection.target && !visited.has(connection.target.id)) {
                queue.push(connection.target);
            }
        }
    }

    return undefined;
}

/**
 * Validates that intermediate return is properly placed before the hydration point.
 * Uses recursive traversal to check all execution paths.
 * 
 * Rules:
 * - Intermediate return can be directly attached to the hydration point
 * - OR all execution paths must satisfy the requirement:
 *   - Exclusive decision branches: ALL branches must have IR
 *   - Inclusive/Parallel branches: At least ONE branch must have IR
 * 
 * Returns error message if validation fails, null if valid.
 */
function validateIntermediateReturnBeforeHydrationPoint(
    start: BpmnJsElement | undefined,
    hydrationPoint: BpmnJsElement,
    elementsToValidate: Array<IElement>
): string | null {
    const isIntermediateReturn = (bpmnElement: BpmnJsElement | undefined): boolean => {
        if (bpmnElement === undefined) {
            return false;
        }

        const element = elementsToValidate.find(e => e.id === bpmnElement.id);
        return element?.type === ElementType.IntermediateReturn;
    };

    if (start === undefined) {
        return null;
    }

    // Check if hydration point has intermediate return directly attached
    const outgoing = hydrationPoint.outgoing || [];
    for (const connection of outgoing) {
        if (isIntermediateReturn(connection.target)) {
            return null;
        }
    }

    // Recursively validate the path from start to hydration point
    const isValid = hasIntermediateReturnBeforeTarget(start, hydrationPoint, elementsToValidate, isIntermediateReturn, new Set());

    if (!isValid) {
        return "All execution branches must have an Intermediate Return before the Checkpoint.";
    }

    return null;
}

/**
 * Recursively validates that a path from current element to target contains an intermediate return.
 * Handles splits by applying appropriate logic (AND for exclusive, OR for inclusive/parallel branches).
 * 
 * Returns true if the path satisfies the requirement.
 */
function hasIntermediateReturnBeforeTarget(
    current: BpmnJsElement | undefined,
    target: BpmnJsElement,
    elementsToValidate: Array<IElement>,
    isIntermediateReturn: (element: BpmnJsElement | undefined) => boolean,
    visited: Set<string>
): boolean {
    // Avoid cycles
    if (current === undefined || visited.has(current.id)) {
        return false;
    }

    const newVisited = new Set(visited);
    newVisited.add(current.id);

    if (isIntermediateReturn(current)) {
        return true; // Found intermediate return - this path is satisfied
    }

    if (current.id === target.id) {
        return false; // Reached target without finding IR - path not satisfied
    }

    const outgoing = current.outgoing || [];

    if (outgoing.length === 0) {
        return false; // Dead end - path not satisfied
    }

    // Check if any immediate outgoing connection is to an IR
    for (const connection of outgoing) {
        if (isIntermediateReturn(connection.target)) {
            return true;
        }
    }

    // Single path - continue recursively  
    if (outgoing.length === 1) {
        return hasIntermediateReturnBeforeTarget(outgoing[0].target, target, elementsToValidate, isIntermediateReturn, newVisited);
    }

    // Multiple non-IR outgoing connections - this is a split point
    const isExclusive = current.type === "bpmn:ExclusiveGateway";
    const branchResults: boolean[] = [];

    // Validate each branch recursively
    for (const connection of outgoing) {
        const branchValid = hasIntermediateReturnBeforeTarget(connection.target, target, elementsToValidate, isIntermediateReturn, newVisited);
        branchResults.push(branchValid);
    }

    if (isExclusive) {
        // Exclusive: ALL branches must have IR (since only one executes, but we don't know which)
        return branchResults.every(result => result);
    } else {
        // Inclusive/Parallel: At least ONE branch must have IR (all branches execute together)
        return branchResults.some(result => result);
    }
}

function hasUpstreamMatch(element: BpmnJsElement, match: (el: BpmnJsElement) => boolean, visited: Set<BpmnJsElement> = new Set()): boolean {
    return !!findUpstreamMatch(element, match, visited);
}

// Returns first upstream match
function findUpstreamMatch(element: BpmnJsElement, match: (el: BpmnJsElement) => boolean, visited: Set<BpmnJsElement> = new Set()): BpmnJsElement | undefined {
    if (visited.has(element)) {
        return undefined;
    }
    visited.add(element);

    if (match(element)) {
        return element;
    }

    if (element.incoming?.length) {
        for (const connection of element.incoming) {
            if (!connection.source) continue;
            const result = findUpstreamMatch(connection.source, match, visited);
            if (result) {
                return result;
            }
        }
    }

    return undefined;
}

function followParallelBranch(bpmnJsElement: BpmnJsElement, visited: Set<string>, elements: Array<IElement>): BpmnJsElement[] {
    const path: BpmnJsElement[] = [];
    const stack = [bpmnJsElement];

    while (stack.length > 0) {
        const current = stack.pop();

        if (!current || visited.has(current.id)) {
            continue;
        }

        visited.add(current.id);
        path.push(current);

        const incoming = getValidParallelBranchTraversalConnections(current, "incoming");

        // If the current element has multiple incoming arrows, it's a merge point
        // The branch ends here, don't follow it further
        if (incoming.length > 1) {
            continue;
        }

        // If the current element is Scheduled Hydration, don't follow it further
        // Arrow between Scheduled Hydration Point is only for visualization purposes and ignored in process compilation
        if (getIntermediateCatchEventEventDefinitionType(current) === ElementType.ScheduledRehydration) {
            continue;
        }

        // Ignore branches with Intermediate return
        const elementConfig = elements.find(e => e.id === current.id);
        if (elementConfig && elementConfig.type === ElementType.IntermediateReturn) {
            return [];
        }

        const outgoing = getValidParallelBranchTraversalConnections(current, "outgoing");

        // Simple linear continuation: follow the single outgoing arrow
        if (outgoing.length === 1 && outgoing[0].target) {
            stack.push(outgoing[0].target);
        }
        else if (outgoing.length > 1) {
            // Sub-branch split
            for (const arrow of outgoing) {
                if (arrow.target) {
                    const subPath = followParallelBranch(arrow.target, new Set(visited), elements);
                    path.push(...subPath);
                }
            }
        }
    }

    return path;
}

function getValidParallelBranchTraversalConnections(currentElement: BpmnJsElement, direction: ConnectionDirection): BpmnJsConnectionElement[] {

    // Exclude connections from exclusive gateway as those are not executed in parallel
    if (currentElement.type === "bpmn:ExclusiveGateway" && direction === "outgoing") {
        return [];
    }

    const connectionElements = direction === "incoming" ? currentElement.incoming : currentElement.outgoing;

    if (!connectionElements) {
        return [];
    }

    const connectionsToIgnore = ["bpmn:DataInputAssociation", "bpmn:DataOutputAssociation", "bpmn:Association"];

    // Ignores connections to/from documentation nodes as those are ignored in process compilation
    return connectionElements.filter(conn => !connectionsToIgnore.includes(conn.type));
}

function validateProcess(process: Process): ValidationError[] {
    const result: ValidationError[] = [];
    if (process.name == null || process.name === "") { // The process should have a name
        result.push(new ValidationError(ValidationErrorType.NoProcessName, "process"));
    }

    return result;
}

export function validateElementNamesAndState(state: State, elementsToValidate: Array<IElement>): ValidationError[] {
    const result: ValidationError[] = [];

    const elementsWithMissingRequiredName = elementsToValidate.filter((element) => _.includes(TypesWithRequiredNames, element?.type) && !element?.name); // All elements which requires names should have names
    elementsWithMissingRequiredName.forEach((element) => {
        addNameValidationError(result, ValidationErrorType.NoElementName, element?.id);
    });

    const elementsWithIncompatibleTask = elementsToValidate.filter((element) =>
        element?.type === ElementType.Task
        && !state.tasks.find((t) => t.uri === element.selectedTypeId
            && (state.process.targetFramework === "net471" || t.isStandard || t.isDotNet)));
    elementsWithIncompatibleTask.forEach((element) => {
        addNameValidationError(result, ValidationErrorType.IncompatibleTask, element?.id);
    });

    const elementsWithMissingSelectedType = elementsToValidate.filter((element) => _.includes(TypesWithSelectedType, element?.type) && !element?.selectedTypeId); // All shapes which requires a selected type should have a selected type
    elementsWithMissingSelectedType.forEach((element) => {
        let validationErrorType: ValidationErrorType | null = null;
        switch (element?.type) {
            case ElementType.Task:
                validationErrorType = ValidationErrorType.TaskSelectionMissing;
                break;
            case ElementType.GlobalErrorHandler:
            case ElementType.CallActivity:
                validationErrorType = ValidationErrorType.SubprocessSelectionMissing;
                break;
            case ElementType.Start:
                validationErrorType = ValidationErrorType.TriggerSelectionMissing;
                break;
        }
        addNameValidationError(result, validationErrorType, element?.id);
    });

    // All elements which has a unique name constrain should have unique names.
    // This check should be done against the whole state, not just the elements to validate
    const elementsThatShouldHaveUniqueNames = Object.keys(state.elements)
        .map((k) => state.elements[k])
        .filter((element) => TypesWithUniqueNames.indexOf(element.type) >= 0 && element.name);

    const duplicateNames = getElementsWithDuplicateNames(elementsThatShouldHaveUniqueNames);
    duplicateNames.forEach((element) =>
        addNameValidationError(result, ValidationErrorType.DuplicateName, element.id)
    );

    const decisionBranchStartElements = elementsToValidate.filter((element) => element?.type === ElementType.InclusiveGateway || element?.type === ElementType.Decision);

    for (const d of decisionBranchStartElements) // decision branch names should be unique within the inclusive/exclusive gateway, but does not have to be globaly unique
    {
        const element = BpmnJsCliWrapper.element(d?.id);
        const branches: IElement[] = [];
        if (element?.outgoing) {
            for (const branch of element.outgoing) {
                if (state.elements[branch.id]) {
                    branches.push(state.elements[branch.id]);
                }
            }
        }

        const duplicateBranchNames = getElementsWithDuplicateNames(branches);
        for (const duplicate of duplicateBranchNames) {
            addNameValidationError(result, ValidationErrorType.DuplicateName, duplicate.id);
        }

        if (d?.type === ElementType.Decision && branches.length !== 2) { // Exclusive gateways should have exactly 2 connections:
            addNameValidationError(result, ValidationErrorType.ExclusiveGatewayOutgoingMissing, d?.id);
        }
    }
    return result;
}

export const elementNameKey = "name"; // FRP-1481: The key for specifying element name validation errors
export function addNameValidationError(errors: ValidationError[], type: ValidationErrorType | null, id?: string) {
    errors.push(new ValidationError(type, id, elementNameKey));
}

const hasProcessVarRef = (value: unknown): boolean => {
    if (typeof value === 'string' && value.toLowerCase().startsWith('#var.')) {
        return true;
    }
    if (value && typeof value === 'object') {
        return Object.values(value).some(v => hasProcessVarRef(v));
    }
    return false;
};

export function validateTriggers(elements: Array<IElement>, state: State): ValidationError[] {
    const validationErrors: ValidationError[] = [];
    const triggers = elements
        .filter((element): element is IElement => element?.type === ElementType.Start && element != undefined);

    // Check for process variables in all triggers: FRP-2238
    triggers.forEach(trigger => {
        if (trigger.parameters) {
            Object.entries(trigger.parameters).forEach(([key, value]) => {
                if (hasProcessVarRef(value)) {
                    validationErrors.push(new ValidationError(
                        ValidationErrorType.ProcessVariablesInTriggersNotAllowed,
                        trigger.id,
                        key,
                        '#var'
                    ));
                }
            });
        }
    });

    runTriggerValidators({ state, triggers, validationErrors });
    
    return validationErrors;
}

/**
 * Returns the elements whose name is a duplicate.
 * The check is case-insensitive, and ignores empty and null values
 * @param elements
 */
function getElementsWithDuplicateNames(elements: IElement[]) {
    const nameCounts: { [key: string]: number } = {};
    for (const e of elements) {
        if (e.name) {
            const nameKey = e.name.toLowerCase();
            const newCount = (nameCounts[nameKey] || 0) + 1;
            nameCounts[nameKey] = newCount;
        }
    }

    const duplicateNames = Object.keys(nameCounts)
        .filter((k) => nameCounts[k] > 1);

    if (duplicateNames.length <= 0) {
        return [];
    }

    return elements.filter((e) => e.name && duplicateNames.indexOf(e.name.toLowerCase()) >= 0);
}

export function validateParameterReferences(state: State, elementsToValidate: Array<IElement>): ValidationError[] {
    let result = [] as ValidationError[];
    const scopeDictionary = state.validation.variableScopeDictionary;
    for (const scopeId of Object.keys(scopeDictionary)) {
        const scope = scopeDictionary[scopeId];
        const errors = validateVariableNaming(scope);
        if (errors.length > 0) {
            result = result.concat(errors);
        }
    }

    const startElements = [];
    for (const elementId of Object.keys(state.elements)) {
        const element = state.elements[elementId];
        if (element.type === ElementType.Start) {
            startElements.push(element);
        }
    }
    const manualTriggerParameterNames = state.process.manualTrigger && state.process.manualTrigger.map((t) => t.name);
    // Only run the reference validation on elements marked for validation:
    const referenceErrors = validateReferences(
        elementsToValidate,
        scopeDictionary,
        state.environmentVariables,
        getProcessReferences(state.uiState.tcpTriggerEnabled),
        getTriggerReferences(startElements, manualTriggerParameterNames, state.process.isForMonitoringRule),
        state.processVariables?.map(processVariable => processVariable.name));
    result = result.concat(referenceErrors);
    return result;
}

function validateVariableNaming(variableScope: VariableScope): ValidationError[] {
    const ret = [] as ValidationError[];

    const distinctVariableNames = _.uniq(variableScope.variables.map((v) => v.variableName));

    for (const name of distinctVariableNames) {
        const scopeDeclarations = variableScope.variables.filter((v) => v.variableName === name);
        if (scopeDeclarations.length > 1) { // The variable exist more than once in the scope.
            // if all the declarations is from assign, then that's ok. Otherwise there's a problem
            if (!_.every(scopeDeclarations, (s) => s.elementType === ElementType.Expression)) {
                for (const declaration of scopeDeclarations) {
                    ret.push(new ValidationError(ValidationErrorType.DuplicateVariableName, declaration.elementId, declaration.parameterKey));
                }
                // No need to check if there's more conflicts
                continue;
            }
        }

        const inheritedDeclarations = variableScope.inheritedVariables.filter((v) => v.variableName === name);
        if (inheritedDeclarations.length > 0) { // The variable is declared outside of the scope.
            // if all the declarations is from assign, then that's ok. Otherwise there's a problem
            const inheritedIsAssign = _.every(inheritedDeclarations, (s) => s.elementType === ElementType.Expression);
            const localIsAssign = _.every(scopeDeclarations, (s) => s.elementType === ElementType.Expression);
            if (!(inheritedIsAssign && localIsAssign)) {
                // Only assign the errors to the variables of the scope
                for (const declaration of scopeDeclarations) {
                    ret.push(new ValidationError(ValidationErrorType.DuplicateVariableName, declaration.elementId, declaration.parameterKey));
                }
            }
        }
    }
    for (const variable of variableScope.missingVariableNames) {
        ret.push(new ValidationError(ValidationErrorType.VariableNameMissing, variable.elementId, variable.parameterKey));
    }

    variableScope.variables
        .filter(variable => !VariableNameHasOnlyValidChars(variable.variableName))
        .map(variable => ret.push(new ValidationError(ValidationErrorType.InvalidVariableName, variable.elementId, undefined, undefined, 'Invalid character in variable name')));

    return ret;
}

// https://docs.microsoft.com/en-us/dotnet/csharp/language-reference/language-specification/lexical-structure#identifiers
const validVariableNameRegex = new RegExp(/^[\p{Lu}\p{Ll}\p{Lt}\p{Lm}\p{Lo}\p{Nl}\p{Mn}\p{Mc}\p{Nd}\p{Pc}\p{Cf}]*$/u);

export function VariableNameHasOnlyValidChars(variableName: string | null): boolean {
    if (variableName) {
        return validVariableNameRegex.test(variableName);
    }

    return false;
}

function validateReferences(elements: Array<IElement>, variables: IVariableScopeDictionary, environmentVariables: string[], processReferences: string[], triggerReferences: string[], processVariables: string[]): ValidationError[] {
    let ret = [] as ValidationError[];
    for (const element of elements) {
        const errors = checkParameterReferenceForElement(element, variables, environmentVariables, processReferences, triggerReferences, processVariables);
        if (errors.length > 0) {
            ret = ret.concat(errors);
        }
    }
    return ret;
}

function checkParameterReferenceForElement(element: IElement, variableScope: IVariableScopeDictionary, environmentVariables: string[], processReferences: string[], triggerReferences: string[], processVariables: string[]): ValidationError[] {
    let ret = [] as ValidationError[];
    // Check what's accesible within the scope:
    const locallyAccessibleReturns = [] as IElementVariable[];
    const locallyAccessibleVariables = [] as IElementVariable[];
    if (element?.type === ElementType.GlobalErrorHandler) {
        locallyAccessibleVariables.push({ elementType: ElementType.GlobalErrorHandler, elementId: "globalErrorHandler", variableName: "error", parameterKey: "error" });
    } else {
        populateAccessibleReturnsAndVariables(element?.id, variableScope, locallyAccessibleReturns, locallyAccessibleVariables);
    }
    if (element?.parameters) {
        for (const key of Object.keys(element.parameters)) {
            const parameterValue = element.parameters[key];
            if (_.isObject(parameterValue) || _.isArray(parameterValue)) {
                const errors = checkParameterReference(element.id, parameterValue, locallyAccessibleReturns, locallyAccessibleVariables, environmentVariables, processReferences, triggerReferences, processVariables, key);
                if (errors.length > 0) {
                    ret = ret.concat(errors);
                }
            } else if (_.isString(parameterValue) && !isManualTrigger(element)) {
                const errors = checkSingleParameterReference(element.id, parameterValue, locallyAccessibleReturns, locallyAccessibleVariables, environmentVariables, processReferences, triggerReferences, processVariables, key);
                if (errors.length > 0) {
                    ret = ret.concat(errors);
                }
            }
        }
    }

    return ret;
}
// Using for example #result.ToString() causes issues if everything is done with one regexp.
const resultReferenceRegexp = /(#result)(?:(?:\[([^\]]*)\]))?/ig;

// Match #<something> with an optional following of .<something> or [<something>].
const otherReferenceRegex = /((?:#process)|(?:#var))(?:(?:\.([\w_åÅäÄöÖ]+)))?/ig;

// Copied from Frends.ProcessCompilation.ProcessReferenceExpander
// Uses negative lookahead for a valid match imediately followed by a paranthesis. If that's true, discard the match.
const envOrTriggerReferenceRegex = /(#(env|trigger)(?:(\.[\wåÅäÄöÖ]+)(?![\wåÅäÄöÖ]*\()){1,2})/ig;

function checkParameterReference(elementId: string, parameter: { [key: string]: any }, accessibleReturns: IElementVariable[], accesibleVariables: IElementVariable[], environmentVariables: string[], processReferences: string[], triggerReferences: string[], processVariables: string[], key: string): ValidationError[] {
    let ret = [] as ValidationError[];
    if (!(_.isObject(parameter) || _.isArray(parameter))) {
        return ret;
    }
    if ("mode" in parameter && "value" in parameter && _.isString((parameter as any)["value"])) {
        const parameterValue = (parameter as any)["value"];
        const parameterErrors = checkSingleParameterReference(elementId, parameterValue, accessibleReturns, accesibleVariables, environmentVariables, processReferences, triggerReferences, processVariables, key);
        if (parameterErrors.length > 0) {
            ret = ret.concat(parameterErrors);
        }
    } else if (_.isArray(parameter)) {
        for (const i in parameter) {
            const parameterErrors = checkParameterReference(elementId, parameter[i], accessibleReturns, accesibleVariables, environmentVariables, processReferences, triggerReferences, processVariables, key + "[" + i + "]");
            if (parameterErrors.length > 0) {
                ret = ret.concat(parameterErrors);
            }
        }
    } else {
        for (const parameterKey of Object.keys(parameter)) {
            const parameterValue = (parameter as HashMap<any>)[parameterKey];
            if (_.isObject(parameterValue) || _.isArray(parameterValue)) {
                const parameterErrors = checkParameterReference(elementId, (parameter as HashMap<any>)[parameterKey], accessibleReturns, accesibleVariables, environmentVariables, processReferences, triggerReferences, processVariables, key + "." + parameterKey);
                if (parameterErrors.length > 0) {
                    ret = ret.concat(parameterErrors);
                }
            } else if (_.isString(parameterValue)) {
                const parameterErrors = checkSingleParameterReference(elementId, parameterValue, accessibleReturns, accesibleVariables, environmentVariables, processReferences, triggerReferences, processVariables, key);
                if (parameterErrors.length > 0) {
                    ret = ret.concat(parameterErrors);
                }
            }
        }
    }
    return ret;
}

const checkSingleParameterReference = (elementId: string, parameterValue: any, accessibleReturns: IElementVariable[], accesibleVariables: IElementVariable[], environmentVariables: string[], processReferences: string[], triggerReferences: string[], processVariables: string[], key: string) => {
    const ret = [] as ValidationError[];
    if (isManualTriggerDefaultValueKey(key)) {
        // We'll skip all ManualTrigger default value validations. This check is bit redundant but shouldn't cause issues
        return ret;
    }

    let refMatches;

    // Check result reference reference:
    while ((refMatches = resultReferenceRegexp.exec(parameterValue))) {
        const refString = refMatches[2];
        // TODO: Make sure there's an actual return modeled before the current shape. Currently only named returns are tracked. #result is always valid as of now.
        const foundMatch = (refString === undefined ? true : !!accessibleReturns.find((r) => r.variableName === refString));
        if (!foundMatch) {
            ret.push(new ValidationError(ValidationErrorType.InvalidReference, elementId, key, refMatches[0]));
        }
    }

    // Check the other references:
    while ((refMatches = otherReferenceRegex.exec(parameterValue))) {
        const refType = refMatches[1];
        const refString = refMatches[2] || refMatches[3];
        let foundMatch = true; // In case none of the cases below are true, pretend all is well.

        if (refString === undefined && (refType === "#var" || refType === "#env" || refType === "#process" || refType === "#trigger")) {
            // All references require at least two parts and should be marked as invalid if they are not found
            foundMatch = false;
        } else if (refType === "#var") {
            foundMatch = !!(accesibleVariables.find((r) => r.variableName === refString) || processVariables?.find((pv) => pv === refString)); // variable names *are* case sensitive
        } else if (refType === "#process") {
            foundMatch = !!processReferences.find((p) => p.toLowerCase() === refString.toLowerCase());
        }

        if (!foundMatch) {
            ret.push(new ValidationError(ValidationErrorType.InvalidReference, elementId, key, refMatches[0]));
        }
    }

    while ((refMatches = envOrTriggerReferenceRegex.exec(parameterValue))) {
        const refType = refMatches[2];
        const refString = refMatches[1];
        const refWithoutPrefix = refString.substr(refString.indexOf(".") + 1);
        let foundMatch = true; // In case none of the cases below are true, pretend all is well.
        if (refType === "env") {
            foundMatch = !!environmentVariables.find((r) => matchesEnvVarOrTriggerDataRef(r, refWithoutPrefix));
        } else if (refType === "trigger") {
            foundMatch = !!triggerReferences.find((p) => matchesEnvVarOrTriggerDataRef(p, refWithoutPrefix));
        }

        if (!foundMatch) {
            ret.push(new ValidationError(ValidationErrorType.InvalidReference, elementId, key, refMatches[0]));
        }
    }
    return ret;
}

const isManualTrigger = (element: IElement) => {
    return element.type === ElementType.Start && element.selectedTypeId === "ManualTrigger";
}

export function matchesEnvVarOrTriggerDataRef(varName: string, reference: string) {
    if (!varName || !reference) {
        return false;
    }

    return varName.toLowerCase() === reference.toLowerCase();
}

/** ElementTypes which should skip validation. */
const unvalidatableElementTypes = [
    ElementType.DataObjectReference,
    ElementType.DataStoreReference
]

function getElementsToValidate(elements: IElementDictionary, elementIdsToValidate: string[]): Array<IElement> {
    return Object.keys(elements).map((k) => {
        const element = elements[k];

        if (elementIdsToValidate.indexOf(element.id) >= 0 && !unvalidatableElementTypes.includes(element.type)) {
            return element;
        }
    }).filter((x) => !!x); // filter out undefined;
}

function getElementParametersToValidate(elements: IElementDictionary, elementIdsToValidate: string[]): Array<IElement> {
    const elementIdParts = elementIdsToValidate.map((id) => id.split(".", 2));

    const fullElementIds = elementIdParts.filter((idParts) => idParts.length === 1).map((idParts) => idParts[0]);
    const onlyParameterElementIds = elementIdParts
        .filter((idParts) =>
            idParts.length === 2 && // has element id with parameter key
            fullElementIds.indexOf(idParts[0]) < 0); // element not already fully validated

    if (onlyParameterElementIds.length === 0) {
        return [];
    }

    const parameterKeysByElement = _.groupBy(onlyParameterElementIds, (idParts) => idParts[0]);
    const generatedElementParameterStructures = Object.keys(parameterKeysByElement).map((elementId) => {
        const element = elements[elementId];
        if (!element) {
            // state must be out of sync, ignore for now
            return null;
        }
        const elementWithParametersToValidate: IElement = {
            id: element.id,
            type: element.type,
            parameters: {}
        };
        for (const paramElementIdParts of onlyParameterElementIds) {
            if (paramElementIdParts[0] === elementId) {
                const paramName = paramElementIdParts[1];
                elementWithParametersToValidate.parameters[paramName] = element.parameters[paramName];
            }
        }
        return elementWithParametersToValidate;
    }).filter((x) => !!x); // filter out nulls

    return generatedElementParameterStructures;
}

export function validateHydrationPointAndSignalRehydrationCorrelationIds(elementsToValidate: Array<IElement>): ValidationError[] {
    const errors: ValidationError[] = [];

    const hydrationPoints = elementsToValidate.filter(element => element?.type === ElementType.HydrationPoint || element?.type === ElementType.SignalRehydration);

    for (const element of hydrationPoints) {
        const correlationIdParam = element?.parameters?.["correlationId"];
        const correlationIdValue = correlationIdParam?.value;

        if (!correlationIdValue || (typeof correlationIdValue === "string" && correlationIdValue.trim() === "")) {
            errors.push(new ValidationError(
                element?.type === ElementType.HydrationPoint ? ValidationErrorType.HydrationPointMissingCorrelationId : ValidationErrorType.SignalResumeMissingCorrelationId,
                element.id
            ));
        }
    }

    return errors;
}
